import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NewRelicService } from 'src/config/newrelic.service';
import { environment } from 'src/environments/environment';

type NormalizedError = {
  error: Error | string;
  message: string;
  stack?: string;
  original: unknown;
};

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  private newRelicService?: NewRelicService;

  constructor(private injector: Injector) {
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => this.reportWindowError(event));
      window.addEventListener('unhandledrejection', (event) => this.reportUnhandledRejection(event));
    }
  }

  handleError(error: unknown): void {
    if (!environment.production) {
      console.error(error);
    }
    this.reportError(error, 'angular');
  }

  private reportWindowError(event: ErrorEvent): void {
    const error = event.error ?? new Error(event.message);
    this.reportError(error, 'window', {
      errorFile: event.filename ?? '',
      errorLine: event.lineno ?? '',
      errorColumn: event.colno ?? '',
    });
  }

  private reportUnhandledRejection(event: PromiseRejectionEvent): void {
    const error = event.reason ?? 'Unhandled Promise rejection';
    this.reportError(error, 'unhandledrejection');
  }

  private reportError(error: unknown, source: string, attributes: Record<string, unknown> = {}): void {
    const normalized = this.normalizeError(error);
    if (!normalized || this.isHttpError(normalized.original)) {
      return;
    }

    if (this.shouldIgnoreError(normalized.message)) {
      return;
    }

    if (this.isAlreadyReported(normalized.error)) {
      return;
    }

    this.markReported(normalized.error);

    this.getNewRelicService().noticeError(normalized.error, {
      errorSource: source,
      errorMessage: normalized.message,
      errorStack: normalized.stack ?? '',
      ...attributes,
    });
  }

  private getNewRelicService(): NewRelicService {
    this.newRelicService ??= this.injector.get(NewRelicService);
    return this.newRelicService;
  }

  private normalizeError(error: unknown): NormalizedError | null {
    const extracted = (error as { rejection?: unknown })?.rejection ?? error;

    if (extracted instanceof HttpErrorResponse) {
      return null;
    }

    if (extracted instanceof Error) {
      return {
        error: extracted,
        message: extracted.message || 'Unknown error',
        stack: extracted.stack,
        original: extracted,
      };
    }

    if (extracted && typeof (extracted as { message?: string }).message === 'string') {
      const message = (extracted as { message: string }).message;
      const created = new Error(message);
      const stack = typeof (extracted as { stack?: string }).stack === 'string'
        ? (extracted as { stack: string }).stack
        : undefined;
      created.stack = stack ?? created.stack;
      return {
        error: created,
        message,
        stack: created.stack,
        original: extracted,
      };
    }

    if (typeof extracted === 'string') {
      return {
        error: extracted,
        message: extracted,
        original: extracted,
      };
    }

    if (extracted === null || extracted === undefined) {
      return null;
    }

    const fallback = new Error('Unknown error');
    return {
      error: fallback,
      message: fallback.message,
      stack: fallback.stack,
      original: extracted,
    };
  }

  private isHttpError(error: unknown): boolean {
    return error instanceof HttpErrorResponse;
  }

  private shouldIgnoreError(message: string): boolean {
    const ignoredPatterns = [
      'NG0953',
      'ResizeObserver loop',
      'Script error.',
    ];

    return ignoredPatterns.some((pattern) => message.includes(pattern));
  }

  private isAlreadyReported(error: Error | string): boolean {
    if (typeof error === 'string') {
      return false;
    }

    return Boolean((error as Error & { __nrReported?: boolean }).__nrReported);
  }

  private markReported(error: Error | string): void {
    if (typeof error === 'string') {
      return;
    }

    (error as Error & { __nrReported?: boolean }).__nrReported = true;
  }
}
